import React, { type MouseEvent, useContext } from 'react';

import type { AnalyticsEventPayload } from '@atlaskit/analytics-next/AnalyticsEvent';
import type { CardProviderProps as ProviderProps } from '@atlaskit/link-provider/types';
import type { AuthorityUiMetadata } from '@atlassian/conversation-assistant-service-api/types/assistance-service';
import type { LuminaSubAgentResponseType } from '@atlassian/lumina';

import { AiBrand, CardTemplate, QueryIntent } from '../constants';
import type { DetectionMetadata, IntentClassificationData } from '../query-intent-api/types';

import type { NLPSearchDisclaimerType } from './nlp-search-disclaimer-type';
import type {
	AIAnswerCacheType,
	Callbacks,
	commonAttributesType,
	NLPDynamicLayoutSubAgentResponse,
	NLPSearchType,
	ReadingAidsOptions,
	SAINResearchStep,
	TraceHeaders,
} from './types';
import { UNINITIALIZED_COMMON_ATTRIBUTES } from './uninitialized-common-attributes';

export interface AIAnswerContextType {
	loading?: boolean;
	/**
	 * This is the partial response that is shown when a streaming response is received.
	 *
	 * This will only be present when the loading prop is true.
	 *
	 * When a partialResponse is received -- it is expected the full response will be
	 * received in the same format.
	 */
	isAnswered: boolean;
	hasError: boolean;
	onNavigate: (e: MouseEvent) => void;
	updateContextCache: (data: Partial<AIAnswerCacheType>) => void;
	fireAnalyticsEvent: (payload: AnalyticsEventPayload) => void;
	setQuery: (value: string) => void;
	setData: React.Dispatch<React.SetStateAction<NLPSearchType>>;
	userDetails: {
		id?: string;
		name?: string;
	};
	disclaimer: NLPSearchDisclaimerType | null;
	commonAttributes: commonAttributesType;
	setLiked: (liked: boolean | undefined) => void;
	likesDisabled: boolean;
	setLikesDisabled: (likesDisabled: boolean) => void;
	advancedSearchUrl?: string;
	partialResponse?: { format: 'MARKDOWN'; partialNlpResult: string };
	dynamicLayoutSubAgentMessages: NLPDynamicLayoutSubAgentResponse[] | LuminaSubAgentResponseType[];
	setDynamicLayoutSubAgentMessages: React.Dispatch<React.SetStateAction<any[]>>;
	onClose?: () => void;
	initialCollapsed?: boolean;
	liked?: boolean;
	initialReported?: boolean;
	fromFollowUp?: boolean;
	isReadingAids?: boolean;
	readingAidsOptions?: ReadingAidsOptions;
	cardProviderProps?: Omit<ProviderProps, 'children'>;
	baseQuery?: string;
	smartAnswersRoute?: QueryIntent;
	queryIntentClassificationData?: IntentClassificationData;
	queryIntentDetectionMetadata?: DetectionMetadata;
	queryIntentDetectionTraceId?: string;
	cardTemplate?: CardTemplate;
	brand: AiBrand;
	answerEditor?: {
		name: string;
		id: string;
	};
	closeParentDialog?: () => void;
	additionalQueryContext?: string;
	callbacks?: Callbacks;
	isAdminHubAIEnabled?: boolean;
	definitionToggleEnabled?: boolean;
	sourceProduct?: string;
	showChatFollowUps?: boolean;
	queryReasoning?: string;
	/**
	 * Structured research steps derived from `RESEARCH_STEP_*` trace events.
	 * Populated when BE returns the Think-Deeper-Messages payload (via the
	 * `X-Think-Deeper-Messages-Opt-In` / `X-New-Tool-Traces-Opt-In` headers).
	 */
	researchSteps?: { [stepId: string]: SAINResearchStep };
	showGenericFooter?: boolean;
	initiallyExpanded?: boolean;
	traceHeaders?: TraceHeaders;
	luminaContentMap?: Record<string, string>;
	/**
	 * Authority UI metadata from the final response, present when the BE has
	 * assessed the answer as highly authoritative.
	 */
	authorityUiMetadata?: AuthorityUiMetadata | null;
}

export const AIAnswerContext: React.Context<AIAnswerContextType> =
	React.createContext<AIAnswerContextType>({
		loading: undefined,
		isAnswered: false,
		hasError: false,
		advancedSearchUrl: undefined,
		onNavigate: () => {},
		updateContextCache: () => {},
		fireAnalyticsEvent: () => {},
		setQuery: () => {},
		setData: () => {},
		userDetails: {},
		disclaimer: null,
		commonAttributes: UNINITIALIZED_COMMON_ATTRIBUTES,
		initialCollapsed: undefined,
		liked: undefined,
		setLiked: () => {},
		likesDisabled: false,
		setLikesDisabled: () => {},
		initialReported: undefined,
		isReadingAids: false,
		dynamicLayoutSubAgentMessages: [],
		setDynamicLayoutSubAgentMessages: () => {},
		cardProviderProps: undefined,
		baseQuery: undefined,
		smartAnswersRoute: QueryIntent.DEFAULT,
		cardTemplate: CardTemplate.DEFAULT,
		brand: AiBrand.AI,
		answerEditor: undefined,
		closeParentDialog: () => {},
		showGenericFooter: false,
		initiallyExpanded: false,
		queryIntentClassificationData: undefined,
		queryIntentDetectionMetadata: undefined,
		queryIntentDetectionTraceId: undefined,
		traceHeaders: undefined,
	});

// eslint-disable-next-line @atlaskit/volt-strict-mode/no-multiple-exports
export const useAIAnswerContext: () => AIAnswerContextType = () => {
	return useContext(AIAnswerContext);
};
